import { AtticScope, CellarScope, MeasurementOnSiteAdditionalInformationType, MeasurementOnSitePropertyType } from './measurementDTO'

import { FeeDTO } from 'models/fee'
import { Nullable } from 'models/helpers'

/**
 * Request of the stateless measurement quote endpoint (POST /order/configuration/measurement/quote, ISSUE-2184).
 * Mirrors the measurement configuration minus the room count; `numberOfResidentialUnits` is required
 * for the unit-based categories (Kat. 3/4 — SEMI_DETACHED_HOUSE, APARTMENT_BUILDING) and rejected for
 * the rest (the request is polymorphic by `propertyType` on the backend, see backend-platform#64).
 */
export interface MeasurementQuoteRequestDTO {
  /** Property category being measured */
  propertyType: MeasurementOnSitePropertyType
  /** Total surface area to measure in m² — must be greater than zero */
  surfaceArea: number
  /** Number of floors to measure (ground floor counts as 1) — at least 1 */
  numberOfFloors: number
  /** Whether the basement/cellar area is included in the measurement — deprecated on the BE in favour of cellarScope; must equal the scope's measured-ness when the scope is sent */
  basementAreaToMeasure: boolean
  /** Whether the attic area is included in the measurement — deprecated on the BE in favour of atticScope; must equal the scope's measured-ness when the scope is sent */
  atticAreaToMeasure: boolean
  /** Cellar scope/presence (ISSUE-2459) — ROOM and PRESENT_NOT_MEASURED price at €0; when omitted the BE derives it from basementAreaToMeasure */
  cellarScope?: CellarScope
  /** Attic scope/presence (ISSUE-2459) — PRESENT_NOT_MEASURED prices at €0; when omitted the BE derives it from atticAreaToMeasure */
  atticScope?: AtticScope
  /** Number of residential units — required for the unit-based categories (Kat. 3/4), at least 1; rejected for the rest */
  numberOfResidentialUnits?: number
  /** Express Service add-on (ISSUE-2838) — priced by the engine as its own line item */
  expressService?: boolean
  /** Additional House Views add-on (ISSUE-2838) — priced by the engine as its own line item */
  additionalHouseViews?: boolean
  /** Optional context for the measurement; does not affect pricing */
  additionalInformation?: MeasurementOnSiteAdditionalInformationType[]
  /** Optional free-text comment; does not affect pricing */
  comment?: string
}

/** Machine-readable measurement quote line item type */
export enum MeasurementQuoteLineItemType {
  CATEGORY_BASE = 'CATEGORY_BASE',
  ADDITIONAL_FLOOR = 'ADDITIONAL_FLOOR',
  RESIDENTIAL_UNIT = 'RESIDENTIAL_UNIT',
  CELLAR = 'CELLAR',
  ATTIC = 'ATTIC',
  AREA_SURCHARGE = 'AREA_SURCHARGE',
  /** Express Service add-on (ISSUE-2838) */
  EXPRESS_SERVICE = 'EXPRESS_SERVICE',
  /** Additional House Views add-on (ISSUE-2838) */
  ADDITIONAL_HOUSE_VIEWS = 'ADDITIONAL_HOUSE_VIEWS',
}

/** A single cost component of a measurement quote */
export interface MeasurementQuoteLineItemDTO {
  /** Machine-readable line item type */
  type: MeasurementQuoteLineItemType
  /** Human-readable label; clients may localise by type */
  label: string
  /** Quantity for quantised items (e.g. number of additional floors); null otherwise */
  quantity: Nullable<number>
  /** Per-unit price (flat fee, or per-m² rate); null when not applicable */
  unitPrice: Nullable<FeeDTO>
  /** Net amount for this line */
  amount: FeeDTO
}

/** Stateless price estimate for an On-Site Area Report measurement configuration */
export interface MeasurementQuoteResponseDTO {
  /** Currency of all monetary values (e.g. "EUR") */
  currency: string
  /** Applied VAT rate as a percentage (e.g. 19.0) */
  vatRate: number
  /** Individual cost components that sum to the net total */
  lineItems: MeasurementQuoteLineItemDTO[]
  /** Net total (sum of line item amounts) */
  net: FeeDTO
  /** VAT amount (net * vatRate / 100) */
  vat: FeeDTO
  /** Gross total (net + VAT) */
  gross: FeeDTO
}
